/**
 * Industry Benchmark Data for Seertech
 * 
 * Sources:
 * - ATD State of the Industry Report (2023-2024)
 * - Brandon Hall Group Learning & Development Benchmarking Study
 * - McKinsey & Company: The economic case for reskilling
 * - Phillips ROI Institute: ROI Methodology Standards
 * - Bersin by Deloitte: High-Impact Learning Organization Research
 * - LinkedIn Learning Workplace Learning Report
 * 
 * Note: These benchmarks combine research-backed data with model-derived
 * estimates based on learning elasticity theory. They should be used as
 * directional guidance, not absolute standards.
 */

// Industry types with descriptions
export const INDUSTRY_TYPES = [
  { id: 'technology', label: 'Technology', description: 'Software, IT Services, Hardware' },
  { id: 'healthcare', label: 'Healthcare', description: 'Hospitals, Pharma, Medical Devices' },
  { id: 'financial', label: 'Financial Services', description: 'Banking, Insurance, Investment' },
  { id: 'manufacturing', label: 'Manufacturing', description: 'Industrial, Consumer Goods, Automotive' },
  { id: 'retail', label: 'Retail & Consumer', description: 'E-commerce, Brick & Mortar, CPG' },
  { id: 'professional', label: 'Professional Services', description: 'Consulting, Legal, Accounting' },
  { id: 'education', label: 'Education', description: 'Higher Ed, K-12, EdTech' },
  { id: 'government', label: 'Government & Public', description: 'Federal, State, Non-profit' },
];

// Company size tiers
export const COMPANY_SIZES = [
  { id: 'smb', label: 'SMB', description: 'Under 500 employees', range: '< 500' },
  { id: 'midmarket', label: 'Mid-Market', description: '500 - 5,000 employees', range: '500-5K' },
  { id: 'enterprise', label: 'Enterprise', description: 'Over 5,000 employees', range: '5K+' },
];

// Benchmark data by industry and company size
// Values represent typical ranges for ROI (as decimal, e.g., 150 = 1.5x), 
// KPI uplift (%), training spend per employee ($), payback months
export const BENCHMARK_DATA = {
  technology: {
    smb: {
      roi: { p25: 120, p50: 200, p75: 350, p90: 500 },
      kpiUplift: { p25: 3, p50: 6, p75: 12, p90: 18 },
      spendPerEmployee: { p25: 800, p50: 1200, p75: 1800, p90: 2500 },
      paybackMonths: { p25: 18, p50: 12, p75: 8, p90: 5 },
    },
    midmarket: {
      roi: { p25: 150, p50: 250, p75: 400, p90: 600 },
      kpiUplift: { p25: 4, p50: 8, p75: 14, p90: 22 },
      spendPerEmployee: { p25: 1000, p50: 1500, p75: 2200, p90: 3000 },
      paybackMonths: { p25: 16, p50: 10, p75: 6, p90: 4 },
    },
    enterprise: {
      roi: { p25: 180, p50: 300, p75: 450, p90: 700 },
      kpiUplift: { p25: 5, p50: 10, p75: 16, p90: 25 },
      spendPerEmployee: { p25: 1200, p50: 1800, p75: 2800, p90: 4000 },
      paybackMonths: { p25: 14, p50: 9, p75: 5, p90: 3 },
    },
  },
  healthcare: {
    smb: {
      roi: { p25: 100, p50: 170, p75: 280, p90: 400 },
      kpiUplift: { p25: 2, p50: 5, p75: 9, p90: 14 },
      spendPerEmployee: { p25: 600, p50: 1000, p75: 1500, p90: 2000 },
      paybackMonths: { p25: 20, p50: 14, p75: 10, p90: 6 },
    },
    midmarket: {
      roi: { p25: 130, p50: 220, p75: 350, p90: 500 },
      kpiUplift: { p25: 3, p50: 7, p75: 12, p90: 18 },
      spendPerEmployee: { p25: 800, p50: 1300, p75: 1900, p90: 2600 },
      paybackMonths: { p25: 18, p50: 12, p75: 8, p90: 5 },
    },
    enterprise: {
      roi: { p25: 160, p50: 270, p75: 420, p90: 600 },
      kpiUplift: { p25: 4, p50: 9, p75: 15, p90: 22 },
      spendPerEmployee: { p25: 1000, p50: 1600, p75: 2400, p90: 3200 },
      paybackMonths: { p25: 16, p50: 10, p75: 6, p90: 4 },
    },
  },
  financial: {
    smb: {
      roi: { p25: 140, p50: 230, p75: 380, p90: 550 },
      kpiUplift: { p25: 3, p50: 7, p75: 12, p90: 18 },
      spendPerEmployee: { p25: 900, p50: 1400, p75: 2000, p90: 2800 },
      paybackMonths: { p25: 16, p50: 11, p75: 7, p90: 4 },
    },
    midmarket: {
      roi: { p25: 170, p50: 280, p75: 450, p90: 650 },
      kpiUplift: { p25: 4, p50: 9, p75: 15, p90: 23 },
      spendPerEmployee: { p25: 1100, p50: 1700, p75: 2500, p90: 3500 },
      paybackMonths: { p25: 14, p50: 9, p75: 5, p90: 3 },
    },
    enterprise: {
      roi: { p25: 200, p50: 330, p75: 520, p90: 750 },
      kpiUplift: { p25: 5, p50: 11, p75: 18, p90: 28 },
      spendPerEmployee: { p25: 1400, p50: 2100, p75: 3200, p90: 4500 },
      paybackMonths: { p25: 12, p50: 8, p75: 4, p90: 2 },
    },
  },
  manufacturing: {
    smb: {
      roi: { p25: 90, p50: 150, p75: 250, p90: 380 },
      kpiUplift: { p25: 2, p50: 4, p75: 8, p90: 12 },
      spendPerEmployee: { p25: 500, p50: 800, p75: 1200, p90: 1600 },
      paybackMonths: { p25: 22, p50: 16, p75: 11, p90: 7 },
    },
    midmarket: {
      roi: { p25: 120, p50: 200, p75: 320, p90: 480 },
      kpiUplift: { p25: 3, p50: 6, p75: 11, p90: 16 },
      spendPerEmployee: { p25: 700, p50: 1100, p75: 1600, p90: 2200 },
      paybackMonths: { p25: 20, p50: 13, p75: 9, p90: 5 },
    },
    enterprise: {
      roi: { p25: 150, p50: 250, p75: 400, p90: 580 },
      kpiUplift: { p25: 4, p50: 8, p75: 14, p90: 20 },
      spendPerEmployee: { p25: 900, p50: 1400, p75: 2100, p90: 2900 },
      paybackMonths: { p25: 18, p50: 11, p75: 7, p90: 4 },
    },
  },
  retail: {
    smb: {
      roi: { p25: 80, p50: 140, p75: 230, p90: 350 },
      kpiUplift: { p25: 2, p50: 4, p75: 7, p90: 11 },
      spendPerEmployee: { p25: 400, p50: 700, p75: 1000, p90: 1400 },
      paybackMonths: { p25: 24, p50: 18, p75: 12, p90: 8 },
    },
    midmarket: {
      roi: { p25: 110, p50: 180, p75: 300, p90: 450 },
      kpiUplift: { p25: 3, p50: 5, p75: 10, p90: 15 },
      spendPerEmployee: { p25: 600, p50: 950, p75: 1400, p90: 1900 },
      paybackMonths: { p25: 22, p50: 15, p75: 10, p90: 6 },
    },
    enterprise: {
      roi: { p25: 140, p50: 230, p75: 370, p90: 540 },
      kpiUplift: { p25: 4, p50: 7, p75: 12, p90: 18 },
      spendPerEmployee: { p25: 800, p50: 1200, p75: 1800, p90: 2500 },
      paybackMonths: { p25: 20, p50: 13, p75: 8, p90: 5 },
    },
  },
  professional: {
    smb: {
      roi: { p25: 130, p50: 220, p75: 360, p90: 520 },
      kpiUplift: { p25: 3, p50: 6, p75: 11, p90: 17 },
      spendPerEmployee: { p25: 1000, p50: 1500, p75: 2200, p90: 3000 },
      paybackMonths: { p25: 17, p50: 11, p75: 7, p90: 4 },
    },
    midmarket: {
      roi: { p25: 160, p50: 270, p75: 430, p90: 620 },
      kpiUplift: { p25: 4, p50: 8, p75: 14, p90: 21 },
      spendPerEmployee: { p25: 1200, p50: 1900, p75: 2800, p90: 3800 },
      paybackMonths: { p25: 15, p50: 9, p75: 6, p90: 3 },
    },
    enterprise: {
      roi: { p25: 190, p50: 320, p75: 500, p90: 720 },
      kpiUplift: { p25: 5, p50: 10, p75: 17, p90: 26 },
      spendPerEmployee: { p25: 1500, p50: 2300, p75: 3400, p90: 4800 },
      paybackMonths: { p25: 13, p50: 8, p75: 5, p90: 3 },
    },
  },
  education: {
    smb: {
      roi: { p25: 70, p50: 120, p75: 200, p90: 300 },
      kpiUplift: { p25: 2, p50: 4, p75: 7, p90: 11 },
      spendPerEmployee: { p25: 400, p50: 650, p75: 950, p90: 1300 },
      paybackMonths: { p25: 26, p50: 20, p75: 14, p90: 9 },
    },
    midmarket: {
      roi: { p25: 100, p50: 170, p75: 280, p90: 420 },
      kpiUplift: { p25: 3, p50: 5, p75: 9, p90: 14 },
      spendPerEmployee: { p25: 550, p50: 900, p75: 1300, p90: 1800 },
      paybackMonths: { p25: 24, p50: 17, p75: 11, p90: 7 },
    },
    enterprise: {
      roi: { p25: 130, p50: 220, p75: 350, p90: 500 },
      kpiUplift: { p25: 4, p50: 7, p75: 12, p90: 18 },
      spendPerEmployee: { p25: 700, p50: 1100, p75: 1700, p90: 2400 },
      paybackMonths: { p25: 22, p50: 14, p75: 9, p90: 5 },
    },
  },
  government: {
    smb: {
      roi: { p25: 60, p50: 100, p75: 170, p90: 260 },
      kpiUplift: { p25: 1, p50: 3, p75: 6, p90: 9 },
      spendPerEmployee: { p25: 350, p50: 550, p75: 850, p90: 1200 },
      paybackMonths: { p25: 30, p50: 24, p75: 18, p90: 12 },
    },
    midmarket: {
      roi: { p25: 80, p50: 140, p75: 230, p90: 350 },
      kpiUplift: { p25: 2, p50: 4, p75: 8, p90: 12 },
      spendPerEmployee: { p25: 500, p50: 800, p75: 1200, p90: 1600 },
      paybackMonths: { p25: 28, p50: 20, p75: 14, p90: 9 },
    },
    enterprise: {
      roi: { p25: 100, p50: 180, p75: 290, p90: 430 },
      kpiUplift: { p25: 3, p50: 5, p75: 10, p90: 15 },
      spendPerEmployee: { p25: 650, p50: 1050, p75: 1550, p90: 2100 },
      paybackMonths: { p25: 26, p50: 18, p75: 12, p90: 7 },
    },
  },
};

// LEI Improvement tier definitions (model-derived)
export const LEI_TIERS = [
  { 
    id: 'conservative', 
    label: 'Conservative', 
    range: '5-10%', 
    minLei: 5, 
    maxLei: 10,
    description: 'Incremental improvements from targeted training interventions',
    expectedRoi: '0.8x - 1.5x',
    typicalScenarios: ['Compliance training refresh', 'Minor process updates', 'Tool adoption']
  },
  { 
    id: 'moderate', 
    label: 'Moderate', 
    range: '10-20%', 
    minLei: 10, 
    maxLei: 20,
    description: 'Significant skill development with measurable behavior change',
    expectedRoi: '1.5x - 3x',
    typicalScenarios: ['Leadership development', 'Sales methodology', 'Technical upskilling']
  },
  { 
    id: 'aggressive', 
    label: 'Aggressive', 
    range: '20-35%', 
    minLei: 20, 
    maxLei: 35,
    description: 'Transformational programs with comprehensive reinforcement',
    expectedRoi: '3x - 6x',
    typicalScenarios: ['Digital transformation', 'Culture change', 'New role onboarding']
  },
  { 
    id: 'breakthrough', 
    label: 'Breakthrough', 
    range: '35%+', 
    minLei: 35, 
    maxLei: 100,
    description: 'Exceptional results requiring optimal conditions and execution',
    expectedRoi: '6x+',
    typicalScenarios: ['Strategic capability building', 'High-potential acceleration', 'Critical skill gaps']
  },
];

// Research sources for citations
export const RESEARCH_SOURCES = [
  {
    id: 'atd2024',
    title: 'ATD State of the Industry Report',
    year: '2024',
    organization: 'Association for Talent Development',
    keyFindings: 'Average direct learning expenditure per employee was $1,220. Organizations spent an average of 57 learning hours per employee.',
    url: 'https://www.td.org/research-reports/state-of-the-industry'
  },
  {
    id: 'brandonhall',
    title: 'Learning & Development Benchmarking Study',
    year: '2023',
    organization: 'Brandon Hall Group',
    keyFindings: 'High-performing organizations are 2.4x more likely to measure learning ROI and report average ROI of 2.5x-3.5x for strategic programs.',
    url: 'https://www.brandonhall.com'
  },
  {
    id: 'mckinsey',
    title: 'The Economic Case for Reskilling',
    year: '2023',
    organization: 'McKinsey & Company',
    keyFindings: 'Companies that invest in comprehensive reskilling programs see productivity gains of 6-12% and retention improvements of 15-25%.',
    url: 'https://www.mckinsey.com'
  },
  {
    id: 'phillips',
    title: 'ROI Methodology Standards',
    year: '2023',
    organization: 'Phillips ROI Institute',
    keyFindings: 'When properly isolated, learning programs typically achieve ROI between 100-300%, with top performers exceeding 500%.',
    url: 'https://roiinstitute.net'
  },
  {
    id: 'bersin',
    title: 'High-Impact Learning Organization Research',
    year: '2023',
    organization: 'Bersin by Deloitte',
    keyFindings: 'Organizations with mature learning cultures are 92% more likely to innovate and have 37% higher employee productivity.',
    url: 'https://www.bersin.com'
  },
  {
    id: 'linkedin',
    title: 'Workplace Learning Report',
    year: '2024',
    organization: 'LinkedIn Learning',
    keyFindings: 'Companies that excel at internal mobility retain employees for an average of 5.4 years, nearly 2x longer than those that struggle with it.',
    url: 'https://learning.linkedin.com/resources/workplace-learning-report'
  },
];

// Calculate percentile rank for a given value
export const calculatePercentile = (value, benchmarkData) => {
  if (!benchmarkData) return 50;
  
  const { p25, p50, p75, p90 } = benchmarkData;
  
  if (value <= p25) {
    return Math.round((value / p25) * 25);
  } else if (value <= p50) {
    return Math.round(25 + ((value - p25) / (p50 - p25)) * 25);
  } else if (value <= p75) {
    return Math.round(50 + ((value - p50) / (p75 - p50)) * 25);
  } else if (value <= p90) {
    return Math.round(75 + ((value - p75) / (p90 - p75)) * 15);
  } else {
    return Math.min(99, Math.round(90 + ((value - p90) / p90) * 10));
  }
};

// Get performance tier based on percentile
export const getPerformanceTier = (percentile) => {
  if (percentile >= 90) return { tier: 'top', label: 'Top Performer', color: 'emerald' };
  if (percentile >= 75) return { tier: 'above', label: 'Above Average', color: 'teal' };
  if (percentile >= 50) return { tier: 'average', label: 'Average', color: 'blue' };
  if (percentile >= 25) return { tier: 'below', label: 'Below Average', color: 'amber' };
  return { tier: 'low', label: 'Needs Improvement', color: 'red' };
};

// Get benchmarks for specific industry and size
export const getBenchmarks = (industryId, sizeId) => {
  const industry = BENCHMARK_DATA[industryId] || BENCHMARK_DATA.technology;
  return industry[sizeId] || industry.midmarket;
};

// Get LEI tier for a given improvement percentage
export const getLeiTier = (leiImprovement) => {
  for (const tier of LEI_TIERS) {
    if (leiImprovement >= tier.minLei && leiImprovement < tier.maxLei) {
      return tier;
    }
  }
  return LEI_TIERS[LEI_TIERS.length - 1]; // Return highest tier if above all
};

// Reverse calculation: Get metric value for a target percentile
export const getValueForPercentile = (targetPercentile, benchmarkData) => {
  if (!benchmarkData) return 0;
  
  const { p25, p50, p75, p90 } = benchmarkData;
  
  if (targetPercentile <= 25) {
    return (targetPercentile / 25) * p25;
  } else if (targetPercentile <= 50) {
    return p25 + ((targetPercentile - 25) / 25) * (p50 - p25);
  } else if (targetPercentile <= 75) {
    return p50 + ((targetPercentile - 50) / 25) * (p75 - p50);
  } else if (targetPercentile <= 90) {
    return p75 + ((targetPercentile - 75) / 15) * (p90 - p75);
  } else {
    // Extrapolate beyond P90
    return p90 + ((targetPercentile - 90) / 10) * (p90 * 0.2);
  }
};

// Calculate required LEI improvement to achieve target metric value
// Uses inverse of the impact model: LEI = f^-1(metric)
export const calculateRequiredLei = (currentLei, currentValue, targetValue) => {
  if (currentValue <= 0 || targetValue <= 0) return currentLei;
  
  // Calculate the ratio of target to current
  const ratio = targetValue / currentValue;
  
  // Inverse of diminishing returns formula
  // Original: output = base * (1 + sqrt(lei_factor - 1) * 0.7) for ratio > 1
  // Inverse: lei_factor = ((output/base - 1) / 0.7)^2 + 1
  
  if (ratio <= 1) {
    // Target is at or below current, simple linear scaling
    return Math.max(1, currentLei * ratio);
  } else {
    // Target is above current, need to account for diminishing returns
    // Solve: ratio = 1 + sqrt(x - 1) * 0.7, where x = targetLei / currentLei
    // sqrt(x - 1) = (ratio - 1) / 0.7
    // x - 1 = ((ratio - 1) / 0.7)^2
    // x = ((ratio - 1) / 0.7)^2 + 1
    const leiMultiplier = Math.pow((ratio - 1) / 0.7, 2) + 1;
    return Math.min(100, currentLei * leiMultiplier);
  }
};

// Preset percentile options
export const PERCENTILE_PRESETS = [
  { value: 50, label: '50th (Average)', color: 'blue' },
  { value: 75, label: '75th (Above Avg)', color: 'teal' },
  { value: 90, label: '90th (Top Tier)', color: 'emerald' },
];
